import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Activity,
  Download,
  Heart,
  HeartPulse,
  Stethoscope,
  Thermometer,
  Wind,
  X,
  CheckCircle2,
  AlertTriangle,
  Headphones,
  Volume2,
  ExternalLink,
  FileText,
  Radio,
  Send,
  CheckSquare,
  Square,
  Check,
  Clock,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const TEST_TYPES = [
  { key: "BP", label: "Blood Pressure", icon: HeartPulse, color: "text-rose-600 bg-rose-50" },
  { key: "ECG", label: "ECG", icon: Activity, color: "text-violet-600 bg-violet-50" },
  { key: "SpO2", label: "SpO₂", icon: Wind, color: "text-sky-600 bg-sky-50" },
  { key: "Stethoscope", label: "Stethoscope", icon: Stethoscope, color: "text-emerald-600 bg-emerald-50" },
  { key: "Temperature", label: "Temperature", icon: Thermometer, color: "text-amber-600 bg-amber-50" },
];

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "";
}

function EcgPreview({ samples }) {
  const w = 600, h = 100;
  if (!samples || samples.length === 0) {
    return (
      <div className="h-[100px] w-full rounded-xl bg-secondary/50 border border-border flex items-center justify-center text-xs text-muted-foreground">
        Waiting for waveform…
      </div>
    );
  }
  const points = samples.map((v, i) => {
    const x = (i / Math.max(samples.length - 1, 1)) * w;
    const y = h / 2 - v * (h / 2 - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} className="rounded-xl bg-secondary/50 border border-border" data-testid="ecg-svg">
      <polyline fill="none" stroke="hsl(179 84% 35%)" strokeWidth="1.6" points={points.join(" ")} />
    </svg>
  );
}

function StethoscopeAudioPlayer({ test }) {
  const p = test.payload || test.result_summary || test.summary || {};
  const s = test.summary || test.result_summary || {};
  const mode = p.mode || s.mode || "HEART";
  const position = p.position || s.position || "Mitral Valve (Apex)";
  const duration = p.duration || s.duration || p.result1 || s.result1 || 15;
  const headline = s.headline || p.headline || "";
  const takeMatch = headline.match(/Take\s*(\d+)/i);
  const takeNumber = p.take || s.take || (takeMatch ? takeMatch[1] : 1);
  const dateTime = p.dateTime || s.dateTime || test.completed_at || test.started_at || "";

  const backendBase = (api.defaults?.baseURL || "").replace(/\/api$/, "");
  const directApiUrl = `${backendBase}/api/diagnostic-tests/${test.id}/audio`;
  const downloadApiUrl = `${backendBase}/api/diagnostic-tests/${test.id}/audio?download=1`;
  const audioSrc = p.audio_url || (p.audio_base64 ? `data:audio/wav;base64,${p.audio_base64}` : directApiUrl);

  const handleDownload = (e) => {
    e.preventDefault();
    if (p.audio_base64) {
      const a = document.createElement("a");
      a.href = `data:audio/wav;base64,${p.audio_base64}`;
      a.download = `stethoscope_${test.id}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      window.open(downloadApiUrl, "_blank");
    }
  };

  return (
    <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-4 sm:p-5 space-y-3" data-testid={`steth-player-${test.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm">
            <Headphones className="h-4 w-4" />
          </span>
          <div>
            <div className="font-bold text-sm text-emerald-950 flex items-center gap-2 flex-wrap">
              <span>Recorded Stethoscope Audio</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-700 text-white shadow-xs">
                Take {takeNumber}
              </span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-200/80 text-emerald-800">
                {mode} Mode
              </span>
            </div>
            <div className="text-xs text-emerald-700">
              Placement: <span className="font-semibold">{position}</span> · Duration: <span className="font-semibold">{duration}s</span>
              {dateTime && <span className="ml-2 font-normal text-emerald-600">· 📅 {dateTime}</span>}
            </div>
          </div>
        </div>
        <span className="text-[10px] font-mono font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
          High-Fidelity Audio (.wav)
        </span>
      </div>

      <div className="bg-white rounded-xl p-3 border border-emerald-100 shadow-inner space-y-2">
        <div className="flex items-center justify-between text-xs text-emerald-800 font-medium">
          <span className="inline-flex items-center gap-1.5">
            <Volume2 className="h-3.5 w-3.5 text-emerald-600 animate-pulse" />
            Listen to Patient Heart & Lung Sounds
          </span>
          <span className="font-mono text-[11px] text-muted-foreground">{duration}s Track</span>
        </div>
        <audio
          controls
          preload="auto"
          src={audioSrc}
          className="w-full h-9 rounded-lg outline-none"
          data-testid={`audio-player-${test.id}`}
        />
      </div>

      {p.notes && (
        <div className="text-xs text-emerald-900 bg-white/70 rounded-lg p-2.5 border border-emerald-100/80">
          <span className="font-semibold text-emerald-800">Clinical Finding: </span>
          {p.notes}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2 pt-1 border-t border-emerald-200/60 text-xs">
        <button
          type="button"
          onClick={handleDownload}
          className="font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0"
        >
          <Download className="h-3.5 w-3.5" /> Download Audio File (.wav)
        </button>
        <a
          href={directApiUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-600 hover:underline font-medium inline-flex items-center gap-1.5"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Stream via Direct Link
        </a>
      </div>
    </div>
  );
}

function LivePreview({ test }) {
  if (!test) return null;
  const p = test.payload || {};
  const isSteth = test.test_type === "Stethoscope" || test.test_type === "STETHOSCOPE";

  if (test.test_type === "BP") {
    const pressure = p.cuff_pressure ?? (test.status === "completed" ? `${p.systolic}/${p.diastolic} mmHg` : "—");
    return (
      <div className="space-y-3">
        <div className="bg-secondary/50 rounded-xl p-6 text-center">
          <HeartPulse className="h-10 w-10 text-rose-500 mx-auto mb-2 animate-pulse" />
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{test.status === "completed" ? "Reading" : "Cuff pressure"}</div>
          <div className="text-3xl font-bold tracking-tight mt-1 tabular-nums">{pressure}{typeof pressure === "number" ? " mmHg" : ""}</div>
          {test.status === "completed" && <div className="text-sm text-muted-foreground mt-1">Pulse {p.pulse} bpm</div>}
        </div>
      </div>
    );
  }
  if (test.test_type === "ECG") {
    return (
      <div className="space-y-2">
        <EcgPreview samples={test.status === "completed" ? (p.waveform || []) : (p.waveform_live || [])} />
        {test.status === "completed" && (
          <div className="text-sm">
            <span className="font-semibold">HR {p.heart_rate} bpm</span>{" "}
            <span className="text-muted-foreground">· Rhythm {p.rhythm}</span>
          </div>
        )}
      </div>
    );
  }
  if (test.test_type === "SpO2") {
    const v = test.status === "completed" ? p.spo2 : p.live_spo2;
    return (
      <div className="bg-secondary/50 rounded-xl p-6 text-center">
        <Wind className="h-10 w-10 text-sky-500 mx-auto mb-2 animate-pulse" />
        <div className="text-xs text-muted-foreground uppercase tracking-wide">SpO₂</div>
        <div className="text-3xl font-bold tracking-tight mt-1 tabular-nums">{v ? `${v}%` : "—"}</div>
        {test.status === "completed" && <div className="text-sm text-muted-foreground mt-1">Pulse {p.pulse} bpm</div>}
      </div>
    );
  }
  if (test.test_type === "Temperature") {
    const v = test.status === "completed" ? p.temp_c : p.live_temp;
    return (
      <div className="bg-secondary/50 rounded-xl p-6 text-center">
        <Thermometer className="h-10 w-10 text-amber-500 mx-auto mb-2" />
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Body temperature</div>
        <div className="text-3xl font-bold tracking-tight mt-1 tabular-nums">{v ? `${v}°C` : "—"}</div>
      </div>
    );
  }
  if (isSteth) {
    if (test.status === "completed") {
      return <StethoscopeAudioPlayer test={test} />;
    }
    return (
      <div className="bg-secondary/50 rounded-xl p-6 text-center space-y-2">
        <Heart className="h-10 w-10 text-emerald-500 mx-auto animate-pulse" />
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Auscultation In Progress</div>
        <div className="text-base font-semibold text-emerald-700">Listening to Heart & Lung sounds…</div>
        <div className="text-xs text-muted-foreground">Keep the stethoscope firmly placed on patient chest</div>
      </div>
    );
  }
  return null;
}

export default function ClinicCheckups() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appt, setAppt] = useState(null);
  const [devices, setDevices] = useState([]);
  const [pastTests, setPastTests] = useState([]);
  const [testType, setTestType] = useState("BP");
  const [deviceId, setDeviceId] = useState("");
  const [currentTest, setCurrentTest] = useState(null);
  const [starting, setStarting] = useState(false);
  const [btConnectedDevice, setBtConnectedDevice] = useState(null);
  const [btScanning, setBtScanning] = useState(false);
  const pollRef = useRef(null);

  const requestBluetoothDevice = async () => {
    if (typeof navigator === "undefined" || !navigator.bluetooth) {
      toast.info("Web Bluetooth API is supported on Chrome & Edge. Pre-paired clinic devices are automatically synchronized.");
      return;
    }
    setBtScanning(true);
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ["battery_service", "device_information", "heart_rate", "health_thermometer"],
      });
      if (device) {
        const server = await device.gatt?.connect();
        setBtConnectedDevice({ name: device.name || "Bluetooth Machine", id: device.id, connected: server?.connected ?? true });
        toast.success(`Connected to Bluetooth device: ${device.name || "Machine"}`);
      }
    } catch (err) {
      if (err.name !== "NotFoundError") {
        toast.error(`Bluetooth error: ${err.message}`);
      }
    } finally {
      setBtScanning(false);
    }
  };

  const eligibleDevices = useMemo(
    () => devices.filter((d) => d.supported_tests.includes(testType) && d.status !== "offline"),
    [devices, testType],
  );

  const load = async () => {
    try {
      const [apptRes, dRes, tRes] = await Promise.all([
        api.get(`/appointments/${id}`).catch(() => 
          api.get(`/clinic/me/appointments`).then(r => ({ data: (r.data.items || []).find(x => x.id === id) || null })).catch(() => ({ data: null }))
        ),
        api.get("/clinic/me/devices").catch(() => ({ data: { items: [] } })),
        api.get(`/clinic/me/diagnostic-tests?appointment_id=${id}`).catch(() => ({ data: { items: [] } })),
      ]);
      setAppt(apptRes.data || null);
      setDevices(dRes.data?.items || []);
      const tests = tRes.data?.items || [];
      setPastTests(tests.filter((x) => x.status === "completed" || x.status === "failed" || x.summary || x.result_summary));
    } catch (err) {
      console.error("ClinicCheckups load error:", err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (eligibleDevices.length > 0 && !eligibleDevices.find((x) => x.id === deviceId)) {
      setDeviceId(eligibleDevices[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleDevices]);

  // Poll current test
  useEffect(() => {
    if (!currentTest?.id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/diagnostic-tests/${currentTest.id}`);
        if (!cancelled) {
          setCurrentTest(data);
          if (data.status === "completed" || data.status === "failed") {
            clearInterval(pollRef.current);
            load();
          }
        }
      } catch (_e) { /* */ }
    };
    poll();
    pollRef.current = setInterval(poll, 1000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [currentTest?.id]); // eslint-disable-line

  const startTest = async () => {
    if (!deviceId) return toast.error("Pick a device");
    setStarting(true);
    try {
      const { data } = await api.post("/clinic/me/diagnostic-tests/start", {
        appointment_id: id,
        device_id: deviceId,
        test_type: testType,
      });
      setCurrentTest(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setStarting(false); }
  };

  const cancelTest = async () => {
    if (!currentTest?.id) return;
    try {
      await api.post(`/diagnostic-tests/${currentTest.id}/cancel`);
      toast.success("Test cancelled");
      setCurrentTest(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const [selectedTestIds, setSelectedTestIds] = useState([]);
  const [sendingToDoctor, setSendingToDoctor] = useState(false);

  const toggleSelectTest = (testId) => {
    setSelectedTestIds((prev) =>
      prev.includes(testId) ? prev.filter((x) => x !== testId) : [...prev, testId]
    );
  };

  const toggleSelectAll = () => {
    const unshared = pastTests.filter((t) => !t.shared_with_doctor).map((t) => t.id);
    if (unshared.length === 0) {
      if (selectedTestIds.length === pastTests.length) setSelectedTestIds([]);
      else setSelectedTestIds(pastTests.map((t) => t.id));
      return;
    }
    if (selectedTestIds.length >= unshared.length) {
      setSelectedTestIds([]);
    } else {
      setSelectedTestIds(unshared);
    }
  };

  const handleSendToDoctor = async (idsToSend) => {
    const ids = Array.isArray(idsToSend) ? idsToSend : (selectedTestIds.length > 0 ? selectedTestIds : []);
    if (ids.length === 0) {
      toast.error("Please select at least one test / audio recording to send to doctor.");
      return;
    }
    setSendingToDoctor(true);
    try {
      await api.post("/diagnostic-tests/share-with-doctor", { test_ids: ids, shared: true });
      toast.success(`${ids.length} test recording${ids.length > 1 ? "s" : ""} sent to Doctor successfully!`);
      setSelectedTestIds([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Failed to send to doctor");
    } finally {
      setSendingToDoctor(false);
    }
  };

  const abnormal = currentTest?.summary?.abnormal_flag;

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main data-testid="clinic-checkups-page" className="flex-1 px-6 lg:px-10 py-6 max-w-5xl w-full mx-auto space-y-6">
        <button type="button" onClick={() => navigate(-1)} data-testid="clinic-checkups-back" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        {appt && (
          <div className="bg-white border border-border rounded-2xl p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Appointment</div>
            <div className="font-mono text-sm">{appt.id}</div>
            <div className="mt-1 text-sm">
              <span className="font-semibold">{appt.patient?.name}</span>{" "}
              <span className="text-muted-foreground">with</span>{" "}
              <span className="font-semibold">{appt.doctor?.name}</span>{" "}
              <span className="text-muted-foreground">· {appt.mode.toUpperCase()} · {fmt(appt.scheduled_at)}</span>
            </div>
          </div>
        )}

        {/* Test type & device selectors */}
        <section className="bg-white border border-border rounded-2xl p-5 space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">1. Select test type</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {TEST_TYPES.map((t) => {
                const Icon = t.icon;
                const selected = t.key === testType;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTestType(t.key)}
                    data-testid={`test-type-${t.key}`}
                    className={
                      "border rounded-xl p-3 text-left transition-all " +
                      (selected ? "border-primary ring-2 ring-primary/20 bg-secondary" : "border-border bg-white hover:border-primary/40")
                    }
                  >
                    <span className={"inline-flex h-9 w-9 rounded-lg items-center justify-center " + t.color}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="font-medium text-sm mt-2">{t.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">2. Select device</div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={requestBluetoothDevice}
                disabled={btScanning}
                className="rounded-full text-xs gap-1.5"
              >
                <Activity className="h-3.5 w-3.5 text-primary" />
                {btScanning ? "Scanning Bluetooth…" : btConnectedDevice ? `Connected: ${btConnectedDevice.name}` : "Scan / Connect Bluetooth Machine"}
              </Button>
            </div>
            {eligibleDevices.length === 0 ? (
              <div className="text-xs text-muted-foreground bg-secondary/60 rounded-lg p-3">
                No paired device supports {testType}. Pair a device from the dashboard.
              </div>
            ) : (
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger data-testid="checkups-device-select" className="w-full sm:w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {eligibleDevices.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`checkups-device-option-${d.id}`}>
                      {d.name} · {d.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="pt-2 border-t border-border">
            <Button
              data-testid="checkups-start-test-btn"
              onClick={startTest}
              disabled={starting || !deviceId || (currentTest && currentTest.status !== "completed" && currentTest.status !== "failed")}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
            >
              {starting ? "Starting…" : "Start test"}
            </Button>
          </div>
        </section>

        {/* Live test panel */}
        {currentTest && (
          <section data-testid="live-test-panel" className="bg-white border border-border rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Live · {currentTest.test_type}</div>
                <div className="font-mono text-xs text-muted-foreground">{currentTest.id}</div>
              </div>
              <span
                data-testid={`live-test-status-${currentTest.status}`}
                className={
                  "text-[11px] uppercase tracking-wide font-semibold px-2.5 py-1 rounded-full " +
                  (currentTest.status === "completed"
                    ? "bg-emerald-50 text-emerald-700"
                    : currentTest.status === "failed"
                      ? "bg-rose-50 text-rose-700"
                      : "bg-amber-50 text-amber-700 animate-pulse")
                }
              >
                {currentTest.status}
              </span>
            </div>

            <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
              <div
                data-testid="live-test-progress"
                className="h-full bg-primary transition-[width] duration-500"
                style={{ width: `${currentTest.progress_percent || 0}%` }}
              />
            </div>

            <LivePreview test={currentTest} />

            {currentTest.status === "completed" && currentTest.summary && (
              <div className="rounded-xl p-4 flex items-start gap-3 bg-emerald-50/80 border border-emerald-200/80 text-emerald-950">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" />
                <div>
                  <div className="font-semibold" data-testid="live-test-headline">{currentTest.summary.headline}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Reference range: {currentTest.summary.normal_range}</div>
                </div>
              </div>
            )}

            {currentTest.status === "failed" && (
              <div className="rounded-xl p-4 bg-rose-50 text-rose-900 text-sm">
                {currentTest.error_message || "Test failed."}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
              {currentTest.status === "completed" && (
                <>
                  <Button
                    variant="outline"
                    data-testid="live-test-view-report"
                    onClick={() => openReport(currentTest.id)}
                    className="rounded-full"
                  >
                    <Download className="h-4 w-4 mr-1.5" /> View report
                  </Button>
                  <Button data-testid="live-test-another" onClick={() => setCurrentTest(null)} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">
                    Run another test
                  </Button>
                </>
              )}
              {(currentTest.status === "pairing" || currentTest.status === "running") && (
                <Button
                  variant="outline"
                  data-testid="live-test-cancel"
                  onClick={cancelTest}
                  className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                >
                  <X className="h-4 w-4 mr-1.5" /> Cancel test
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Past tests */}
        {pastTests.length > 0 && (
          <section className="space-y-4 pt-2">
            <div className="flex items-center justify-between flex-wrap gap-3 bg-white border border-border rounded-2xl p-4 shadow-sm">
              <div>
                <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
                  <span>Completed Diagnostic Test Records</span>
                  <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                    {pastTests.length} Record{pastTests.length > 1 ? "s" : ""}
                  </span>
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select recordings with checkboxes to share them with the Doctor during live consultation.
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleSelectAll}
                  className="rounded-full text-xs"
                >
                  {selectedTestIds.length > 0 ? "Deselect All" : "Select All Unsent"}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSendToDoctor()}
                  disabled={selectedTestIds.length === 0 || sendingToDoctor}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold gap-1.5 shadow"
                >
                  <Send className="h-3.5 w-3.5" />
                  {sendingToDoctor ? "Sending..." : `Send to Doctor (${selectedTestIds.length})`}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {pastTests.map((t) => {
                const isSteth = t.test_type === "Stethoscope" || t.test_type === "STETHOSCOPE";
                const isEcg = t.test_type === "ECG" || t.test_type === "12-Lead ECG";
                const isBp = t.test_type === "BP" || t.test_type === "BLOOD_PRESSURE";
                const isSpo2 = t.test_type === "SpO2" || t.test_type === "PULSE_OXIMETER";
                const isTemp = t.test_type === "Temperature" || t.test_type === "THERMOMETER";
                const abnormal = t.summary?.abnormal_flag;
                const isShared = Boolean(t.shared_with_doctor);
                const isSelected = selectedTestIds.includes(t.id);
                const p = t.payload || t.result_summary || {};

                return (
                  <div
                    key={t.id}
                    data-testid={`past-test-${t.id}`}
                    className={`bg-white border rounded-2xl p-5 shadow-sm space-y-4 transition-all ${isSelected ? "border-emerald-500 ring-2 ring-emerald-500/20" : "border-border hover:border-primary/40"}`}
                  >
                    {/* Test Card Header */}
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => toggleSelectTest(t.id)}
                          className="p-1 text-muted-foreground hover:text-emerald-600 transition-colors"
                          title={isSelected ? "Deselect recording" : "Select to send to doctor"}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <Square className="h-5 w-5 text-muted-foreground/60" />
                          )}
                        </button>

                        <span className="h-10 w-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center font-bold">
                          {isSteth ? <Stethoscope className="h-5 w-5 text-emerald-600" /> :
                           isEcg ? <Activity className="h-5 w-5 text-violet-600" /> :
                           isBp ? <HeartPulse className="h-5 w-5 text-rose-600" /> :
                           isSpo2 ? <Wind className="h-5 w-5 text-sky-600" /> :
                           <Thermometer className="h-5 w-5 text-amber-600" />}
                        </span>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-base">{t.test_type}</span>
                            <span className="text-xs text-muted-foreground">· {t.device_name}</span>
                            
                            {isShared ? (
                              <span
                                className="text-[10px] uppercase tracking-wide font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-100 text-emerald-800"
                              >
                                <Check className="h-3 w-3" />
                                Sent to Doctor
                              </span>
                            ) : (
                              <span
                                className="text-[10px] uppercase tracking-wide font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 bg-amber-100 text-amber-800"
                              >
                                <Clock className="h-3 w-3" />
                                Clinic Draft (Not Sent)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Recorded on {fmt(t.started_at)} · ID: <span className="font-mono">{t.id}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {!isShared && (
                          <Button
                            size="sm"
                            onClick={() => handleSendToDoctor([t.id])}
                            disabled={sendingToDoctor}
                            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs font-semibold shadow-sm h-8"
                          >
                            <Send className="h-3 w-3" /> Send to Doctor
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReport(t.id)}
                          className="rounded-full gap-1.5 shadow-sm h-8"
                          data-testid={`past-test-report-btn-${t.id}`}
                        >
                          <FileText className="h-3.5 w-3.5" /> View Official Report
                        </Button>
                      </div>
                    </div>

                    {/* Headline Banner */}
                    {t.summary?.headline && (
                      <div className={"rounded-xl p-3 text-sm font-medium " + (abnormal ? "bg-rose-50/70 text-rose-950 border border-rose-100" : "bg-secondary/40 text-foreground border border-border/50")}>
                        <span className="font-semibold">Result: </span>{t.summary.headline}
                        {t.summary.normal_range && (
                          <span className="text-xs opacity-75 block mt-0.5">Normal range: {t.summary.normal_range}</span>
                        )}
                      </div>
                    )}

                    {/* Specific Detailed Test Visualizations */}
                    {isSteth && (
                      <StethoscopeAudioPlayer test={t} />
                    )}

                    {isEcg && (
                      <div className="space-y-2 bg-secondary/30 rounded-xl p-3 border border-border">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="font-semibold text-foreground">Lead II Waveform Strip</span>
                          <span>HR: {p.heart_rate || p.pulse || 75} bpm · {p.rhythm || "Sinus Rhythm"}</span>
                        </div>
                        <EcgPreview samples={p.waveform || [0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0, 0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0]} />
                      </div>
                    )}

                    {isBp && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-secondary/30 rounded-xl p-3.5 border border-border text-center">
                        <div>
                          <div className="text-[10px] uppercase font-bold text-muted-foreground">Blood Pressure</div>
                          <div className="text-xl font-bold mt-0.5">{p.systolic || p.result1 || 120}/{p.diastolic || p.result2 || 80} <span className="text-xs font-normal">mmHg</span></div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold text-muted-foreground">Pulse Rate</div>
                          <div className="text-xl font-bold mt-0.5">{p.pulse || p.result3 || 72} <span className="text-xs font-normal">bpm</span></div>
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <div className="text-[10px] uppercase font-bold text-muted-foreground">Classification</div>
                          <div className="text-sm font-semibold mt-1 text-primary">{abnormal ? "Requires Monitoring" : "Optimal Reading"}</div>
                        </div>
                      </div>
                    )}

                    {isSpo2 && (
                      <div className="grid grid-cols-2 gap-3 bg-secondary/30 rounded-xl p-3.5 border border-border text-center">
                        <div>
                          <div className="text-[10px] uppercase font-bold text-muted-foreground">Oxygen Saturation</div>
                          <div className="text-xl font-bold mt-0.5">{p.spo2 || p.result1 || 98}%</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold text-muted-foreground">Pulse Rate</div>
                          <div className="text-xl font-bold mt-0.5">{p.pulse || p.result3 || p.result2 || 74} <span className="text-xs font-normal">bpm</span></div>
                        </div>
                      </div>
                    )}

                    {isTemp && (
                      <div className="bg-secondary/30 rounded-xl p-3.5 border border-border text-center">
                        <div className="text-[10px] uppercase font-bold text-muted-foreground">Body Temperature</div>
                        <div className="text-xl font-bold mt-0.5">{p.temp_c || p.result1 || 36.8}°C <span className="text-xs font-normal text-muted-foreground">({((Number(p.temp_c || 36.8) * 9/5) + 32).toFixed(1)}°F)</span></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
